// Mirrored from cloud/functions/src/domain/hardware-names.ts.
// Single source of truth lives there; keep these in sync until both
// codebases consume a shared @ufh/domain workspace package.

// Constitution IV — hardware-truthful naming. These string literals
// MUST match the cable jacket labels at the manifold and the
// wiring centre, and MUST be used identically across every layer
// of the system (firmware identifiers, device-twin JSON keys, IoT
// telemetry, Service Bus message bodies, Cosmos documents, API
// payloads, dashboard labels). Single source of truth.

import { z } from 'zod';

export const FloorName = z.enum(['ground', 'middle', 'top']);
export type FloorName = z.infer<typeof FloorName>;

export const GroupName = z.enum([
  'ground',
  'middle_sun',
  'middle_shade',
  'top_sun',
  'top_shade',
]);
export type GroupName = z.infer<typeof GroupName>;

export const SystemMode = z.enum(['heating', 'cooling']);
export type SystemMode = z.infer<typeof SystemMode>;

// Device-REPORTED / effective mode. Operators only ever select (and the cloud
// only ever pushes as desired) heating|cooling, but a device additionally
// reports "off" while in safe state or holding a stale-config fallback before
// any desired config has landed — see firmware control_task.c
// system_mode_label(). The read model (twin.reported + GET /state) MUST be able
// to represent that, so it cannot reuse the narrower SystemMode.
export const ReportedSystemMode = z.enum(['heating', 'cooling', 'off']);
export type ReportedSystemMode = z.infer<typeof ReportedSystemMode>;

export const FloorCall = z.enum(['none', 'heat', 'cool']);
export type FloorCall = z.infer<typeof FloorCall>;

export const GroupState = z.enum(['closed', 'open', 'transitioning']);
export type GroupState = z.infer<typeof GroupState>;

export const WpmMode = z.enum(['off', 'heating', 'cooling', 'defrost', 'dhw']);
export type WpmMode = z.infer<typeof WpmMode>;


export const LinkHealth = z.enum(['alive', 'timeout']);
export type LinkHealth = z.infer<typeof LinkHealth>;

export const SensorStatus = z.enum([
  'good',
  'tier-a-out-of-envelope',
  'tier-a-open-circuit',
  'tier-a-short-circuit',
  'tier-b-rate-of-change',
  'tier-b-stuck',
]);
export type SensorStatus = z.infer<typeof SensorStatus>;

export const OverrideKind = z.enum([
  'force_open',
  'force_closed',
  'hold_at_setpoint',
]);
export type OverrideKind = z.infer<typeof OverrideKind>;

// The single floor → groups mapping. Every other layer reads from
// here rather than redefining it.
export const FLOOR_TO_GROUPS: Record<FloorName, readonly GroupName[]> = {
  ground: ['ground'],
  middle: ['middle_sun', 'middle_shade'],
  top: ['top_sun', 'top_shade'],
};

export const GROUP_TO_FLOOR: Record<GroupName, FloorName> = {
  ground: 'ground',
  middle_sun: 'middle',
  middle_shade: 'middle',
  top_sun: 'top',
  top_shade: 'top',
};
